import { fromBech32 } from "@cosmjs/encoding";
import { Decimal } from "@cosmjs/math";
import { ChainInfo, Keplr } from "@keplr-wallet/types";
import {
  Account,
  Address,
  Msg,
  Network,
  Signer,
  signers,
  Simulation,
  TxResult,
} from "rujira.js";
import { castSigner, CosmosClient } from "rujira.js/src/signers/cosmos";
import {
  AminoTypes,
  OfflineAminoSigner,
} from "rujira.js/src/signers/cosmos/amino";
import { OfflineDirectSigner } from "rujira.js/src/signers/cosmos/proto-signing";
import { Comet38Client } from "rujira.js/src/signers/cosmos/rpc/comet38";
import { HttpBatchClient } from "rujira.js/src/signers/cosmos/rpc/rpcclients";
import { MsgSend } from "rujira.js/src/signers/cosmos/types/cosmos/bank/v1beta1/tx";
import { MsgExecuteContract } from "rujira.js/src/signers/cosmos/types/cosmwasm/wasm/v1/tx";
import { MsgDeposit } from "rujira.js/src/signers/cosmos/types/thorchain/types/msg_deposit";
import * as config from "../config";

export class CosmosAdapter implements Signer {
  constructor(
    private k: () => Keplr | undefined,
    private opts?: { skipSuggest?: boolean }
  ) {}
  onChange?: ((cb: () => void) => void) | undefined;
  isAvailable(): boolean {
    const k = this.k();
    if (!k) return false;
    if ("isOkxWallet" in k) return false;
    return true;
  }

  public async connect(): Promise<Address[]> {
    const k = this.k();
    if (!k) throw new Error(`Keplr unavailable`);

    return Promise.allSettled(
      getConfigs().map(async (chain) => {
        !this.opts?.skipSuggest && (await k.experimentalSuggestChain(chain));
        await k.enable(chain.chainId);
        const signer = await k.getOfflineSignerAuto(chain.chainId);
        const accounts = await signer.getAccounts();
        return accounts.map((a) => a.address);
      })
    ).then((res) => {
      return res.reduce(
        (a: Address[], v) =>
          v.status === "fulfilled" ? [...v.value, ...a] : a,
        []
      );
    });
  }

  public async simulate(msg: Msg): Promise<Simulation> {
    const client = await this.buildClient(msg.account);
    return simulate(client, msg);
  }

  public async signAndBroadcast(
    _simulation: Simulation,
    msg: Msg
  ): Promise<TxResult> {
    const encoded = await msg.toCosmosTx();
    const client = await this.buildClient(msg.account);
    const res = await client.signAndBroadcast(
      msg.account.address,
      encoded.msgs,
      "auto",
      encoded.memo
    );
    signers.cosmos.assertIsDeliverTxSuccess(res);
    return {
      network: msg.account.network,
      address: msg.account.address,
      txHash: res.transactionHash,
      deposited: msg.toDeposit ? msg.toDeposit() : undefined,
    };
  }

  public async buildClient(account: Account): Promise<CosmosClient> {
    const config = getConfig(fromBech32(account.address).prefix);
    const signer = await this.k()?.getOfflineSignerAuto(config.chainId);
    if (!signer) throw new Error(`No signer available for ${account.address}`);
    return buildClient(config, castSigner(signer));
  }

  public networks(): Network[] {
    return [
      "GAIA",
      "KUJI",
      // "NOBLE",
      "OSMO",
      // "TERRA",
      // "TERRA2",
      "THOR",
    ];
  }
}

export const getGasPrice = (c: ChainInfo): signers.cosmos.GasPrice => ({
  denom: c.feeCurrencies[0].coinDenom,
  amount: Decimal.fromUserInput(
    c.feeCurrencies[0].gasPriceStep?.average.toString() || "0",
    18
  ),
});

export const getNetwork = (c: ChainInfo): Network => {
  switch (c.chainName) {
    case "Kujira":
      return "KUJI";
    case "Cosmos Hub":
      return "GAIA";
    case "Noble":
      return "NOBLE";
    case "Osmosis":
      return "OSMO";
    case "THORChain":
    case "THORChain Stagenet":
    case "THORChain Devnet":
      return "THOR";
    default:
      throw new Error(`No Network for chain ${c.chainName}`);
  }
};

export const getConfigs = (): ChainInfo[] => {
  const mode = import.meta.env.MODE;
  switch (mode) {
    case "main":
    case "stage":
    case "dev":
      return config[mode];
    default:
      throw new Error(`Invalid mode ${mode}`);
  }
};

export const getConfig = (prefix: string): ChainInfo => {
  const c = getConfigs().find(
    (a) => a.bech32Config?.bech32PrefixAccAddr === prefix
  );
  if (!c) throw new Error(`Unrecognised Cosmos prefix ${prefix} `);
  return c;
};

export const buildClient = (
  config: ChainInfo,
  signer: OfflineAminoSigner | OfflineDirectSigner,
  options?: signers.cosmos.CosmosClientOptions
): CosmosClient => {
  const client = Comet38Client.create(new HttpBatchClient(config.rpc));
  const gasPrice = getGasPrice(config);

  return CosmosClient.createWithSigner(client, signer, {
    gasPrice,
    aminoTypes: new AminoTypes({
      ...signers.cosmos.createDefaultAminoConverters(),
      ...signers.cosmos.createThorchainAminoConverters(
        config.chainId === "thorchain-1" ? "thor" : "sthor"
      ),
    }),
    ...options,
  });
};

export const simulate = async (client: CosmosClient, msg: Msg) => {
  const encoded = await msg.toCosmosTx();

  const simRaw = await client.simulate(
    msg.account.address,
    encoded.msgs,
    encoded.memo
  );
  const sim = Math.ceil(simRaw * 1.5);
  const fee = client.calculateFee(sim).amount[0];
  const feeAmount = BigInt(fee.amount);
  if (msg.account.network === "THOR") {
    // Gas is only charged on thorchain if a MsgExecuteContract is in the tx
    const gasCost = encoded.msgs.find(
      (a) => a.typeUrl === MsgExecuteContract.typeUrl
    )
      ? feeAmount
      : 0n;

    // And all 0.02 RUNE fees still apply
    const feeCost =
      BigInt(
        encoded.msgs.filter((a) =>
          [MsgSend.typeUrl, MsgDeposit.typeUrl].includes(a.typeUrl)
        ).length
      ) * 2000000n;

    return {
      symbol: "RUNE",
      decimals: 8,
      amount: feeCost + gasCost,
      gas: BigInt(sim),
    };
  }

  return {
    // KUJI, ATOM, OSMO. All u prefixed and 6 decimals
    symbol: fee.denom.replace("u", "").toUpperCase(),
    decimals: 6,
    amount: feeAmount,
    gas: BigInt(sim),
  };
};
